import { WORLD_COUNTRIES } from './world';
import type { BBox } from './geo';

export interface CountryDefinition {
  /** ISO 3166-1 alpha-2 code (or a well-known territory code like RE). */
  code: string;
  name: string;
  /** ISO 3166-1 numeric id of the Natural Earth geometry to sample from. */
  geometryId: string;
  /** Only sample the parts of the geometry inside this box (e.g. mainland France, not French Guiana). */
  bounds?: BBox;
  /**
   * Relative Street View coverage density. Countries are picked proportionally to
   * sqrt(area) × coverage so small countries still show up and sparse ones don't dominate.
   */
  coverage?: number;
}

/**
 * Countries and territories with official Street View coverage.
 * Countries whose coverage turns out to be missing are skipped automatically by the location pool.
 */
export const COUNTRIES: readonly CountryDefinition[] = [
  // Europe
  { code: 'AD', name: 'Andorra', geometryId: '020' },
  { code: 'AL', name: 'Albania', geometryId: '008', coverage: 0.8 },
  { code: 'AT', name: 'Austria', geometryId: '040', coverage: 1.2 },
  { code: 'BA', name: 'Bosnia and Herzegovina', geometryId: '070', coverage: 0.6 },
  { code: 'BE', name: 'Belgium', geometryId: '056', coverage: 1.2 },
  { code: 'BG', name: 'Bulgaria', geometryId: '100' },
  { code: 'CH', name: 'Switzerland', geometryId: '756', coverage: 1.2 },
  { code: 'CY', name: 'Cyprus', geometryId: '196' },
  { code: 'CZ', name: 'Czechia', geometryId: '203', coverage: 1.2 },
  { code: 'DE', name: 'Germany', geometryId: '276', coverage: 1.2 },
  { code: 'DK', name: 'Denmark', geometryId: '208', coverage: 1.2 },
  { code: 'EE', name: 'Estonia', geometryId: '233' },
  { code: 'ES', name: 'Spain', geometryId: '724', coverage: 1.3 },
  { code: 'FI', name: 'Finland', geometryId: '246' },
  { code: 'FO', name: 'Faroe Islands', geometryId: '234' },
  { code: 'FR', name: 'France', geometryId: '250', bounds: [-5.5, 41.2, 9.8, 51.2], coverage: 1.4 },
  { code: 'GB', name: 'United Kingdom', geometryId: '826', coverage: 1.3 },
  { code: 'GR', name: 'Greece', geometryId: '300' },
  { code: 'HR', name: 'Croatia', geometryId: '191' },
  { code: 'HU', name: 'Hungary', geometryId: '348' },
  { code: 'IE', name: 'Ireland', geometryId: '372' },
  { code: 'IM', name: 'Isle of Man', geometryId: '833' },
  { code: 'IS', name: 'Iceland', geometryId: '352', coverage: 0.8 },
  { code: 'IT', name: 'Italy', geometryId: '380', coverage: 1.3 },
  { code: 'JE', name: 'Jersey', geometryId: '832' },
  { code: 'LI', name: 'Liechtenstein', geometryId: '438' },
  { code: 'LT', name: 'Lithuania', geometryId: '440' },
  { code: 'LU', name: 'Luxembourg', geometryId: '442' },
  { code: 'LV', name: 'Latvia', geometryId: '428' },
  { code: 'ME', name: 'Montenegro', geometryId: '499' },
  { code: 'MK', name: 'North Macedonia', geometryId: '807' },
  { code: 'MT', name: 'Malta', geometryId: '470' },
  { code: 'NL', name: 'Netherlands', geometryId: '528', bounds: [3.2, 50.7, 7.3, 53.7], coverage: 1.2 },
  { code: 'NO', name: 'Norway', geometryId: '578', bounds: [4, 57.8, 31.5, 71.3] },
  { code: 'PL', name: 'Poland', geometryId: '616', coverage: 1.2 },
  { code: 'PT', name: 'Portugal', geometryId: '620', coverage: 1.1 },
  { code: 'RO', name: 'Romania', geometryId: '642' },
  { code: 'RS', name: 'Serbia', geometryId: '688' },
  { code: 'RU', name: 'Russia', geometryId: '643', bounds: [27, 41, 61, 70], coverage: 0.8 },
  { code: 'SE', name: 'Sweden', geometryId: '752' },
  { code: 'SI', name: 'Slovenia', geometryId: '705' },
  { code: 'SK', name: 'Slovakia', geometryId: '703' },
  { code: 'SM', name: 'San Marino', geometryId: '674' },
  { code: 'UA', name: 'Ukraine', geometryId: '804', coverage: 0.8 },

  // North & Central America, Caribbean
  { code: 'US', name: 'United States', geometryId: '840', bounds: [-125, 24.4, -66.9, 49.5], coverage: 2 },
  { code: 'CA', name: 'Canada', geometryId: '124', bounds: [-141, 41.6, -52.6, 62] },
  { code: 'MX', name: 'Mexico', geometryId: '484', coverage: 1.1 },
  { code: 'GT', name: 'Guatemala', geometryId: '320' },
  { code: 'CR', name: 'Costa Rica', geometryId: '188' },
  { code: 'PA', name: 'Panama', geometryId: '591' },
  { code: 'DO', name: 'Dominican Republic', geometryId: '214' },
  { code: 'PR', name: 'Puerto Rico', geometryId: '630' },
  { code: 'VI', name: 'U.S. Virgin Islands', geometryId: '850' },
  { code: 'CW', name: 'Curaçao', geometryId: '531' },
  { code: 'BM', name: 'Bermuda', geometryId: '060' },

  // South America
  { code: 'BR', name: 'Brazil', geometryId: '076', coverage: 1.3 },
  { code: 'AR', name: 'Argentina', geometryId: '032' },
  { code: 'CL', name: 'Chile', geometryId: '152', bounds: [-76, -56, -66, -17] },
  { code: 'CO', name: 'Colombia', geometryId: '170' },
  { code: 'EC', name: 'Ecuador', geometryId: '218' },
  { code: 'PE', name: 'Peru', geometryId: '604', coverage: 0.9 },
  { code: 'BO', name: 'Bolivia', geometryId: '068', coverage: 0.7 },
  { code: 'UY', name: 'Uruguay', geometryId: '858' },
  { code: 'PY', name: 'Paraguay', geometryId: '600', coverage: 0.5 },
  { code: 'FK', name: 'Falkland Islands', geometryId: '238', coverage: 0.4 },

  // Asia & Middle East
  { code: 'JP', name: 'Japan', geometryId: '392', coverage: 1.6 },
  { code: 'KR', name: 'South Korea', geometryId: '410' },
  { code: 'TW', name: 'Taiwan', geometryId: '158' },
  { code: 'HK', name: 'Hong Kong', geometryId: '344' },
  { code: 'MO', name: 'Macao', geometryId: '446' },
  { code: 'TH', name: 'Thailand', geometryId: '764', coverage: 1.1 },
  { code: 'MY', name: 'Malaysia', geometryId: '458' },
  { code: 'SG', name: 'Singapore', geometryId: '702' },
  { code: 'ID', name: 'Indonesia', geometryId: '360' },
  { code: 'PH', name: 'Philippines', geometryId: '608' },
  { code: 'KH', name: 'Cambodia', geometryId: '116', coverage: 0.8 },
  { code: 'LA', name: 'Laos', geometryId: '418', coverage: 0.6 },
  { code: 'VN', name: 'Vietnam', geometryId: '704', coverage: 0.4 },
  { code: 'IN', name: 'India', geometryId: '356', coverage: 0.5 },
  { code: 'BD', name: 'Bangladesh', geometryId: '050', coverage: 0.6 },
  { code: 'LK', name: 'Sri Lanka', geometryId: '144' },
  { code: 'NP', name: 'Nepal', geometryId: '524', coverage: 0.3 },
  { code: 'BT', name: 'Bhutan', geometryId: '064', coverage: 0.6 },
  { code: 'MN', name: 'Mongolia', geometryId: '496', coverage: 0.5 },
  { code: 'KZ', name: 'Kazakhstan', geometryId: '398', coverage: 0.4 },
  { code: 'KG', name: 'Kyrgyzstan', geometryId: '417', coverage: 0.7 },
  { code: 'TR', name: 'Turkey', geometryId: '792', coverage: 0.6 },
  { code: 'IL', name: 'Israel', geometryId: '376' },
  { code: 'PS', name: 'Palestine', geometryId: '275' },
  { code: 'JO', name: 'Jordan', geometryId: '400' },
  { code: 'LB', name: 'Lebanon', geometryId: '422' },
  { code: 'AE', name: 'United Arab Emirates', geometryId: '784' },
  { code: 'QA', name: 'Qatar', geometryId: '634' },
  { code: 'OM', name: 'Oman', geometryId: '512', coverage: 0.4 },

  // Africa
  { code: 'ZA', name: 'South Africa', geometryId: '710', coverage: 1.2 },
  { code: 'LS', name: 'Lesotho', geometryId: '426' },
  { code: 'SZ', name: 'Eswatini', geometryId: '748' },
  { code: 'BW', name: 'Botswana', geometryId: '072', coverage: 0.7 },
  { code: 'NA', name: 'Namibia', geometryId: '516', coverage: 0.5 },
  { code: 'KE', name: 'Kenya', geometryId: '404', coverage: 0.6 },
  { code: 'UG', name: 'Uganda', geometryId: '800', coverage: 0.7 },
  { code: 'RW', name: 'Rwanda', geometryId: '646' },
  { code: 'TZ', name: 'Tanzania', geometryId: '834', coverage: 0.3 },
  { code: 'NG', name: 'Nigeria', geometryId: '566', coverage: 0.4 },
  { code: 'GH', name: 'Ghana', geometryId: '288', coverage: 0.8 },
  { code: 'SN', name: 'Senegal', geometryId: '686', coverage: 0.8 },
  { code: 'TN', name: 'Tunisia', geometryId: '788', coverage: 0.8 },
  { code: 'MG', name: 'Madagascar', geometryId: '450', coverage: 0.3 },
  { code: 'RE', name: 'Réunion', geometryId: '250', bounds: [55.1, -21.5, 56, -20.8] },

  // Oceania
  { code: 'AU', name: 'Australia', geometryId: '036', coverage: 1.1 },
  { code: 'NZ', name: 'New Zealand', geometryId: '554', bounds: [166, -47.5, 179, -34], coverage: 1.1 },
  { code: 'GU', name: 'Guam', geometryId: '316' },
  { code: 'MP', name: 'Northern Mariana Islands', geometryId: '580' },
  { code: 'AS', name: 'American Samoa', geometryId: '016' },
];

const COUNTRIES_BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));

export function getCountry(code: string): CountryDefinition | undefined {
  return COUNTRIES_BY_CODE.get(code);
}

/** A country's English name: the game's own list first, then any country on the world map. */
export function countryName(code: string): string {
  return COUNTRIES_BY_CODE.get(code)?.name ?? WORLD_COUNTRIES[code]?.[1] ?? code;
}
